import {
	CreateDrawerFormProps,
	DateField,
	FormDrawer,
	FormDrawerContent,
	FormDrawerContentProps,
	FormFetchDrawer,
	TextField,
	UpdateDrawerFormProps,
} from "@dgs/core";
import { useFormikContext } from "formik";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { EmployeeSingleSelectField } from "~root/employees/EmployeeSelectField";
import { HalfDayField } from "~root/vacations/HalfDayField";
import { vacationService } from "~shared/api/vacation/vacation_service";
import { IVacationFormState } from "~shared/types";

const useValidate = (isUpdate = false) => {
	const { t } = useTranslation();

	return (values: IVacationFormState) => {
		const errors: { [K in keyof IVacationFormState]?: any } = {};

		if (!values.start) {
			errors.start = t("Field is required");
		}
		if (!values.end) {
			errors.end = t("Field is required");
		}
		if (!isUpdate && !values.employee) {
			errors.employee = t("Field is required");
		}

		return errors;
	};
};

const VacationDrawerContent: FC<FormDrawerContentProps & { isUpdate?: boolean }> = ({ isUpdate, ...props }) => {
	const { t } = useTranslation();
	const { values, setFieldValue } = useFormikContext<IVacationFormState>();

	return (
		<FormDrawerContent {...props}>
			<DateField name="start" label={t("Start")} required />
			<DateField name="end" label={t("End")} required />
			<HalfDayField name="halfDay" label={t("Half day")} />
			<TextField name="remark" label={t("Remark")} />
			{!isUpdate && (
				<EmployeeSingleSelectField
					name="employee"
					label={t("Employee")}
					required
					onChange={(e) => {
						if (e.target.value && values.standIn?.id === e.target.value.id) {
							void setFieldValue("standIn", null);
						}
					}}
				/>
			)}
			<EmployeeSingleSelectField
				name="standIn"
				label={t("Stand in")}
				idsToRemove={values.employee ? [values.employee.id] : []}
			/>
		</FormDrawerContent>
	);
};

export const VacationCreateDrawer: FC<CreateDrawerFormProps<IVacationFormState>> = (drawerProps) => {
	const { t } = useTranslation();
	const validate = useValidate();
	const initialValues: IVacationFormState = {
		start: "",
		end: "",
		remark: "",
		declineReason: null,
		status: "pending",
		halfDay: false,
		employee: null,
		standIn: null,
	};

	return (
		<FormDrawer
			{...drawerProps}
			validate={validate}
			title={t("Add vacation")}
			initialValues={initialValues}
			renderForm={(props) => <VacationDrawerContent {...props} />}
		/>
	);
};

export const VacationUpdateDrawer: FC<UpdateDrawerFormProps<IVacationFormState>> = (drawerProps) => {
	const { t } = useTranslation();
	const validate = useValidate(true);

	return (
		<FormFetchDrawer
			{...drawerProps}
			validate={validate}
			queryFn={() => vacationService.show(drawerProps.id).then((x) => x.data.data)}
			queryKey={vacationService.showKey(drawerProps.id)}
			title={t("Edit vacation")}
			renderSuccess={(props) => <VacationDrawerContent {...props} isUpdate />}
		/>
	);
};
